import React, { useMemo, useState } from "react";
import { Box, Chip, Icon, IconButton, Paper, Typography } from "@mui/material";

export default function ChapterDragDrop({ chapters = [], value = [], onChange }) {
  const appliedIds = useMemo(() => Array.isArray(value) ? value : [], [value]);

  const applied = useMemo(
    () =>
      appliedIds
        .map((id) => chapters.find((c) => c.id === id))
        .filter(Boolean),
    [appliedIds, chapters]
  );

  const appliedIdSet = useMemo(() => new Set(appliedIds), [appliedIds]);

  const available = useMemo(
    () => chapters.filter((c) => !appliedIdSet.has(c.id)),
    [chapters, appliedIdSet]
  );

  const [overIndex, setOverIndex] = useState(null);

  const add = (id) => {
    if (!appliedIdSet.has(id)) onChange([...appliedIds, id]);
  };

  const remove = (id) => {
    onChange(appliedIds.filter((x) => x !== id));
  };

  const reorder = (draggedId, targetIndex) => {
    const fromIndex = appliedIds.indexOf(draggedId);
    if (fromIndex === -1 || fromIndex === targetIndex) return;
    const next = [...appliedIds];
    const [item] = next.splice(fromIndex, 1);
    next.splice(targetIndex, 0, item);
    onChange(next);
  };

  const handleDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", String(id));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDropId = (e) => {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData("text/plain"));
    return Number.isNaN(id) ? null : id;
  };

  return (
    <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: { xs: "wrap", md: "nowrap" } }}>
      <Paper
        variant="outlined"
        sx={{ flex: 1, minWidth: 240, p: 2, maxHeight: 280, overflow: "auto", bgcolor: "#f7f8fc" }}
      >
        <Typography variant="subtitle2" mb={1}>
          Available Chapters
        </Typography>
        {available.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            All chapters applied
          </Typography>
        )}
        {available.map((c) => (
          <Box
            key={c.id}
            draggable
            onDragStart={(e) => handleDragStart(e, c.id)}
            onClick={() => add(c.id)}
            title="Click or drag to apply"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              mb: 0.5,
              border: "1px dashed #b0b7d1",
              borderRadius: 1,
              bgcolor: "#ffffff",
              cursor: "grab",
              "&:hover": { bgcolor: "#eef0f8" },
            }}
          >
            <Icon fontSize="small" sx={{ color: "#7b80a7" }}>
              add_circle_outline
            </Icon>
            <Typography variant="body2">{c.name}</Typography>
          </Box>
        ))}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ flex: 1, minWidth: 240, p: 2, minHeight: 120, bgcolor: "#eef0f8" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          const id = handleDropId(e);
          if (id !== null) add(id);
        }}
      >
        <Typography variant="subtitle2" mb={1}>
          Applied Chapters (drag to reorder)
        </Typography>
        {applied.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Drop or click chapters to apply
          </Typography>
        )}
        {applied.map((c, i) => (
          <Box
            key={c.id}
            draggable
            onDragStart={(e) => handleDragStart(e, c.id)}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIndex(i);
            }}
            onDragEnd={() => setOverIndex(null)}
            onDrop={(e) => {
              const id = handleDropId(e);
              if (id !== null) reorder(id, i);
              setOverIndex(null);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              mb: 0.5,
              border: `2px solid ${overIndex === i ? "#1976d2" : "transparent"}`,
              borderRadius: 1,
              bgcolor: "#ffffff",
              cursor: "grab",
              "&:hover": { boxShadow: 1 },
            }}
          >
            <Chip size="small" color="info" label={i + 1} sx={{ minWidth: 30 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {c.name}
            </Typography>
            <IconButton size="small" onClick={() => remove(c.id)} title="Remove">
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}
